import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Label, Empty } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";
import { ExternalLink, Plus, Smartphone, Check, RefreshCw, Trash2, TrendingUp, TrendingDown, Pencil, X, Users, LineChart, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function AppsPage() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingOne, setRefreshingOne] = useState(null);
  const isAdmin = user?.role !== "customer";

  const load = async () => {
    const { data } = await api.get("/apps");
    setApps(data || []);
  };
  useEffect(() => { load(); if (isAdmin) api.get("/customers").then(({ data }) => setCustomers(data || [])); }, [isAdmin]);

  const refreshOne = async (a) => {
    setRefreshingOne(a.id);
    try {
      await api.post(`/apps/${a.id}/refresh-stats`);
      toast.success(`Refreshed ${a.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingOne(null);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/goodbarber/refresh-all");
      if (data.refreshed > 0) toast.success(`Refreshed ${data.refreshed} apps`);
      if (data.failed?.length) toast.warning(`${data.failed.length} apps failed (check console)`);
      console.log("Refresh all result:", data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteApp = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This also removes it from Firestore.`)) return;
    try {
      await api.delete(`/apps/${a.id}`);
      toast.success("Application deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={isAdmin ? "Delivery · Applications" : "My workspace · Apps"}
        title={isAdmin ? "Applications" : "Your apps"}
        subtitle="Mobile apps linked via GoodBarber and Firebase."
        actions={isAdmin && (
          <>
            <Button variant="secondary" onClick={refreshAll} disabled={refreshing} data-testid="refresh-all-apps-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2}/>
              {refreshing ? "Refreshing…" : "Refresh stats"}
            </Button>
            <Button onClick={() => setShowWizard(true)} data-testid="new-app-btn"><Plus className="w-4 h-4" strokeWidth={2}/>Add application</Button>
          </>
        )}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.map((a) => (
          <div key={a.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 hover:-translate-y-[1px] hover:shadow-[0_12px_32px_-16px_hsl(var(--primary)/0.3)] transition-all group" data-testid="app-card">
            <div className="flex items-center gap-3">
              <img src={a.icon_url} alt="" className="w-12 h-12 rounded-xl object-cover ring-1 ring-[hsl(var(--border))]" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{a.name}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono">v{a.version} · {a.goodbarber_app_id}</div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity">
                  <button onClick={() => setEditing(a)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Edit app" data-testid="edit-app-btn"><Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  <button onClick={() => refreshOne(a)} disabled={refreshingOne === a.id} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Refresh stats"><RefreshCw className={`w-3.5 h-3.5 ${refreshingOne === a.id ? "animate-spin" : ""}`} strokeWidth={1.6}/></button>
                  <button onClick={() => deleteApp(a)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Delete app" data-testid="delete-app-btn"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Stat label="Active users" value={(a.stats?.active_users ?? 0).toLocaleString()} change={a.stats?.active_users_change_pct} />
              <Stat label="Sessions (30d)" value={(a.stats?.sessions_30d ?? 0).toLocaleString()} change={a.stats?.sessions_change_pct} />
              <Stat label="Page views" value={(a.stats?.page_views_30d ?? 0).toLocaleString()} change={a.stats?.page_views_change_pct} />
              <Stat label="Downloads (30d)" value={(a.stats?.downloads_30d ?? 0).toLocaleString()} change={a.stats?.downloads_change_pct} />
            </div>
            {a.stats_updated_at && (
              <div className="mt-3 text-[10px] text-[hsl(var(--text-subtle))] uppercase tracking-widest">Updated {new Date(a.stats_updated_at).toLocaleString()}</div>
            )}
            <TrendsPanel app={a} showComparison={isAdmin} />
            <div className="mt-4 flex gap-2 items-center">
              {a.ios_url && <a target="_blank" rel="noreferrer" href={a.ios_url} className="text-[11px] font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1">iOS <ExternalLink className="w-3 h-3" strokeWidth={1.6}/></a>}
              {a.android_url && <a target="_blank" rel="noreferrer" href={a.android_url} className="text-[11px] font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1">Android <ExternalLink className="w-3 h-3" strokeWidth={1.6}/></a>}
              <div className="ml-auto flex items-center gap-2 text-[11px] text-[hsl(var(--text-muted))]">
                {a.customer_ids?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] font-semibold" title={`Also visible to ${a.customer_ids.length} other customer(s)`} data-testid="app-shared-badge">
                    <Users className="w-3 h-3" strokeWidth={2}/>+{a.customer_ids.length}
                  </span>
                )}
                <span>{a.customer_name}</span>
              </div>
            </div>
          </div>
        ))}
        {apps.length === 0 && <div className="col-span-full"><Empty title="No apps yet" subtitle="Link your first application to start tracking stats." action={isAdmin && <Button onClick={() => setShowWizard(true)}><Plus className="w-4 h-4" strokeWidth={2}/>Add application</Button>}/></div>}
      </div>

      {showWizard && <AppWizard customers={customers} onClose={() => setShowWizard(false)} onCreated={load} />}
      {editing && <AppEditModal app={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TrendsPanel({ app, showComparison }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState("sessions");

  const computedDays = (() => {
    if (days === "custom" && customFrom && customTo) {
      const d = Math.max(1, Math.round((new Date(customTo) - new Date(customFrom)) / 86400000));
      return Math.min(d, 365);
    }
    return typeof days === "number" ? days : 30;
  })();

  const load = async () => {
    if (!app.goodbarber_app_id || !app.app_data_token) { toast.error("This app has no GoodBarber credentials"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/apps/${app.id}/stats-range`, { params: { days: computedDays } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load stats");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, computedDays]);

  if (!app.goodbarber_app_id) return null;
  const series = data ? (metric === "sessions" ? data.history : metric === "page_views" ? data.page_views_history : data.active_users_history) || [] : [];
  const prevSeries = data ? (metric === "sessions" ? data.prev_history : []) || [] : [];

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[hsl(var(--border))]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-[11px] uppercase tracking-widest font-semibold text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] transition-colors" data-testid="trends-toggle">
        <span className="inline-flex items-center gap-1.5"><LineChart className="w-3.5 h-3.5" strokeWidth={2}/>Trends</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" strokeWidth={2}/> : <ChevronDown className="w-3.5 h-3.5" strokeWidth={2}/>}
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="trends-panel">
          <div className="flex flex-wrap items-center gap-1.5">
            {[{ l: "7d", v: 7 }, { l: "30d", v: 30 }, { l: "90d", v: 90 }, { l: "Custom", v: "custom" }].map((r) => (
              <button key={r.l} onClick={() => setDays(r.v)} className={`text-[10px] font-semibold px-2 py-1 rounded border transition-all ${days === r.v ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`} data-testid={`range-${r.l.toLowerCase()}`}>{r.l}</button>
            ))}
          </div>
          {days === "custom" && (
            <div className="flex items-center gap-2 text-[11px]">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[11px]" data-testid="custom-from"/>
              <span className="text-[hsl(var(--text-muted))]">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[11px]" data-testid="custom-to"/>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            {[{ l: "Sessions", v: "sessions" }, { l: "Users", v: "active_users" }, { l: "Page views", v: "page_views" }].map((m) => (
              <button key={m.v} onClick={() => setMetric(m.v)} className={`text-[10px] font-semibold px-2 py-1 rounded transition-all ${metric === m.v ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}>{m.l}</button>
            ))}
          </div>
          {loading ? (
            <div className="h-[120px] flex items-center justify-center text-[11px] text-[hsl(var(--text-muted))]"><RefreshCw className="w-3 h-3 animate-spin mr-2" strokeWidth={2}/>Loading…</div>
          ) : series.length === 0 ? (
            <div className="h-[120px] flex items-center justify-center text-[11px] text-[hsl(var(--text-muted))]">No data for this range.</div>
          ) : (
            <Sparkline series={series} prevSeries={showComparison ? prevSeries : []} />
          )}
          {data && (
            <div className="text-[10px] text-[hsl(var(--text-subtle))] uppercase tracking-widest">
              {data.range_start} → {data.range_end}
              {showComparison && prevSeries.length > 0 && ` · vs ${data.prev_range_start} → ${data.prev_range_end}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Sparkline({ series, prevSeries = [] }) {
  const W = 280, H = 100, P = 4;
  const points = series.map((p) => p.value || 0);
  const prev = prevSeries.map((p) => p.value || 0);
  const allValues = [...points, ...prev];
  const max = Math.max(...allValues, 1);
  const min = Math.min(...allValues, 0);
  const span = max - min || 1;

  const toPath = (values, full) => {
    if (!values.length) return "";
    const step = (W - P * 2) / Math.max(values.length - 1, 1);
    const d = values.map((v, i) => {
      const x = P + i * step;
      const y = H - P - ((v - min) / span) * (H - P * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    if (full) return d + ` L${(P + (values.length - 1) * step).toFixed(1)},${(H - P).toFixed(1)} L${P},${(H - P).toFixed(1)} Z`;
    return d;
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[120px]" data-testid="sparkline">
      <defs>
        <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0"/>
        </linearGradient>
      </defs>
      {prev.length > 0 && <path d={toPath(prev, false)} fill="none" stroke="hsl(var(--text-muted))" strokeWidth="1.3" strokeDasharray="3 3" opacity="0.55"/>}
      <path d={toPath(points, true)} fill="url(#grad)" />
      <path d={toPath(points, false)} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.8"/>
    </svg>
  );
}

function Stat({ label, value, change }) {
  const hasChange = typeof change === "number";
  const up = hasChange && change > 0;
  const down = hasChange && change < 0;
  return (
    <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] px-3 py-2">
      <div className="flex items-baseline justify-between gap-1">
        <div className="font-mono text-[15px] font-semibold tracking-tight">{value}</div>
        {hasChange && change !== null && (
          <div className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${up ? "text-emerald-600 dark:text-emerald-400" : down ? "text-rose-600 dark:text-rose-400" : "text-[hsl(var(--text-muted))]"}`}>
            {up ? <TrendingUp className="w-3 h-3" strokeWidth={2.2}/> : down ? <TrendingDown className="w-3 h-3" strokeWidth={2.2}/> : null}
            {change > 0 ? "+" : ""}{change}%
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] mt-0.5">{label}</div>
    </div>
  );
}

const STEPS = ["General Info", "URLs & Icon", "Firebase Config", "Link to Customer", "Review & Finish"];

function AppWizard({ customers, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "", description: "", version: "1.0.0", app_data_token: "", goodbarber_app_id: "",
    icon_url: "", ios_url: "", android_url: "",
    firebase_project_id: "",
    customer_id: "", customer_name: "", customer_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/apps", {
        ...form,
        stats: { active_users: 0, sessions_30d: 0, push_opens_30d: 0, crash_free_rate: 100 },
      });
      await onCreated();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-[min(760px,100%)] bg-[hsl(var(--surface))] rounded-2xl border border-[hsl(var(--border))] shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="app-wizard">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Wizard</div>
            <h2 className="text-[18px] font-bold">Add application</h2>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">Close</button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${i <= step ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))]"}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold ${i < step ? "bg-[hsl(var(--primary))] text-white" : i === step ? "border-2 border-[hsl(var(--primary))]" : "border border-[hsl(var(--border))]"}`}>
                    {i < step ? <Check className="w-3.5 h-3.5" strokeWidth={2}/> : i + 1}
                  </div>
                  <div className="text-[12px] font-semibold hidden md:block">{s}</div>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--border))]"}`}/>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 min-h-[320px]">
          {step === 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>App name*</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="My Club App"/></div>
              <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description"/></div>
              <div><Label>Version*</Label><Input value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="1.0.0"/></div>
              <div><Label>GoodBarber App ID*</Label><Input value={form.goodbarber_app_id} onChange={(e) => set("goodbarber_app_id", e.target.value)} placeholder="my-club-app"/></div>
              <div className="col-span-2"><Label>App Data Token*</Label><Input value={form.app_data_token} onChange={(e) => set("app_data_token", e.target.value)} placeholder="Paste GoodBarber token"/></div>
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Icon URL</Label><Input value={form.icon_url} onChange={(e) => set("icon_url", e.target.value)} placeholder="https://…/icon.png"/></div>
              <div><Label>iOS App Store URL</Label><Input value={form.ios_url} onChange={(e) => set("ios_url", e.target.value)}/></div>
              <div><Label>Android Play Store URL</Label><Input value={form.android_url} onChange={(e) => set("android_url", e.target.value)}/></div>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 gap-3">
              <div><Label>Firebase Project ID</Label><Input value={form.firebase_project_id} onChange={(e) => set("firebase_project_id", e.target.value)} placeholder="my-firebase-project"/></div>
              <div className="text-[12px] text-[hsl(var(--text-muted))] leading-relaxed bg-[hsl(var(--background))] rounded-lg border border-[hsl(var(--border))] p-3">
                Your Firebase config powers app metadata, notifications, and push history.
                You can paste the full <span className="font-mono">firebase-config.json</span> later from Settings once credentials are provided.
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Primary customer*</Label>
                <Select value={form.customer_id} onChange={(e) => {
                  const c = customers.find((c) => c.id === e.target.value);
                  setForm({ ...form, customer_id: e.target.value, customer_name: c?.company || "", customer_ids: (form.customer_ids || []).filter((id) => id !== e.target.value) });
                }}>
                  <option value="">Select a customer</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
                </Select>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">The app's owner. Billing, ownership, and default dashboards live here.</div>
              </div>
              <div>
                <Label>Also visible to (sponsors / co-owners)</Label>
                <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] min-h-[52px]" data-testid="sponsor-chips">
                  {customers.filter((c) => c.id !== form.customer_id).map((c) => {
                    const on = (form.customer_ids || []).includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => setForm({ ...form, customer_ids: on ? form.customer_ids.filter((id) => id !== c.id) : [...(form.customer_ids || []), c.id] })} className={`text-[12px] px-2.5 py-1 rounded-full border transition-all ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}>
                        {on && <Check className="w-3 h-3 inline mr-1" strokeWidth={2}/>}{c.company}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">These customers will also see this app in their dashboard (e.g. a sponsor of a club app).</div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
                <div className="flex items-center gap-3 mb-3">
                  {form.icon_url ? <img src={form.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover"/> : <div className="w-12 h-12 rounded-lg bg-[hsl(var(--surface-hover))] flex items-center justify-center"><Smartphone className="w-5 h-5 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/></div>}
                  <div>
                    <div className="font-semibold">{form.name || "Untitled"}</div>
                    <div className="text-[12px] text-[hsl(var(--text-muted))] font-mono">v{form.version} · {form.goodbarber_app_id || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div><div className="text-[hsl(var(--text-muted))] uppercase tracking-widest text-[10px]">Firebase</div>{form.firebase_project_id || "—"}</div>
                  <div><div className="text-[hsl(var(--text-muted))] uppercase tracking-widest text-[10px]">Customer</div>{form.customer_name || "—"}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between bg-[hsl(var(--background))]">
          <Button variant="ghost" onClick={prev} disabled={step === 0}>Back</Button>
          <div className="text-[12px] text-[hsl(var(--text-muted))]">Step {step + 1} of {STEPS.length}</div>
          {step < STEPS.length - 1
            ? <Button onClick={next} disabled={step === 0 && (!form.name || !form.version)}>Next</Button>
            : <Button onClick={save} disabled={saving} data-testid="finish-app-wizard">{saving ? "Saving…" : "Finish"}</Button>}
        </div>
      </div>
    </div>
  );
}


function AppEditModal({ app, customers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: app.name || "",
    description: app.description || "",
    version: app.version || "1.0.0",
    goodbarber_app_id: app.goodbarber_app_id || "",
    app_data_token: app.app_data_token || "",
    icon_url: app.icon_url || "",
    ios_url: app.ios_url || "",
    android_url: app.android_url || "",
    firebase_project_id: app.firebase_project_id || "",
    customer_id: app.customer_id || "",
    customer_name: app.customer_name || "",
    customer_ids: app.customer_ids || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/apps/${app.id}`, form);
      toast.success("App updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="app-edit-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">Edit {app.name}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)}/></div>
            <div><Label>Version</Label><Input value={form.version} onChange={(e) => set("version", e.target.value)}/></div>
            <div><Label>GoodBarber App ID</Label><Input value={form.goodbarber_app_id} onChange={(e) => set("goodbarber_app_id", e.target.value)}/></div>
            <div className="col-span-2"><Label>App Data Token</Label><Input value={form.app_data_token} onChange={(e) => set("app_data_token", e.target.value)}/></div>
            <div className="col-span-2"><Label>Icon URL</Label><Input value={form.icon_url} onChange={(e) => set("icon_url", e.target.value)}/></div>
            <div><Label>iOS URL</Label><Input value={form.ios_url} onChange={(e) => set("ios_url", e.target.value)}/></div>
            <div><Label>Android URL</Label><Input value={form.android_url} onChange={(e) => set("android_url", e.target.value)}/></div>
          </div>
          <div>
            <Label>Primary customer</Label>
            <Select value={form.customer_id} onChange={(e) => {
              const c = customers.find((c) => c.id === e.target.value);
              setForm({ ...form, customer_id: e.target.value, customer_name: c?.company || "", customer_ids: (form.customer_ids || []).filter((id) => id !== e.target.value) });
            }}>
              <option value="">(No primary)</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
            </Select>
          </div>
          <div>
            <Label>Also visible to (sponsors / co-owners)</Label>
            <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] min-h-[52px]" data-testid="edit-sponsor-chips">
              {customers.filter((c) => c.id !== form.customer_id).map((c) => {
                const on = (form.customer_ids || []).includes(c.id);
                return (
                  <button key={c.id} type="button" onClick={() => setForm({ ...form, customer_ids: on ? form.customer_ids.filter((id) => id !== c.id) : [...(form.customer_ids || []), c.id] })} className={`text-[12px] px-2.5 py-1 rounded-full border transition-all ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}>
                    {on && <Check className="w-3 h-3 inline mr-1" strokeWidth={2}/>}{c.company}
                  </button>
                );
              })}
              {customers.length <= 1 && <div className="text-[11px] text-[hsl(var(--text-muted))]">Add more customers first to share this app.</div>}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="app-edit-save-btn">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
